import { useState, useEffect, useMemo } from 'react';
import Matter from 'matter-js';
import useBank from './useBank';
import { BET_DENOMINATIONS } from '../utils/GameConstants';

class Particle {
  constructor(x, y, r, world) {
    this.hue = Math.floor(Math.random() * 360);
    const options = {
      restitution: 0.3,
      friction: 0,
      density: 130,
      label: 'particle',
      id: Math.random().toString(36).substr(2, 9)
    };
    this.body = Matter.Bodies.circle(x, y, r, options);
    this.body.label = options.label;
    this.body.id = options.id;
    Matter.Body.setVelocity(this.body, { x: (Math.random() - 0.5) * 2, y: 0 });
    this.r = r;
    Matter.World.add(world, this.body);
  }

  isOffScreen(width, height) {
    const { x, y } = this.body.position;
    return x < -50 || x > width + 50 || y > height;
  }

  show(p5) {
    const { x, y } = this.body.position;
    p5.push();
    p5.translate(x, y);
    p5.fill(this.hue, 255, 255);
    p5.noStroke();
    p5.ellipse(0, 0, this.r * 2);
    p5.pop();
  }
}

class Plinko {
  constructor(x, y, r, world) {
    const options = {
      restitution: 1,
      friction: 0,
      isStatic: true,
      label: 'plinko'
    };
    this.body = Matter.Bodies.circle(x, y, r, options);
    this.r = r;
    Matter.World.add(world, this.body);
  }

  show(p5) {
    const { x, y } = this.body.position;
    p5.push();
    p5.translate(x, y);
    p5.fill(127);
    p5.noStroke();
    p5.ellipse(0, 0, this.r * 2);
    p5.pop();
  }
}

class Boundary {
  constructor(x, y, w, h, world, label) {
    const options = {
      isStatic: true,
      label: label || 'boundary'
    };
    this.body = Matter.Bodies.rectangle(x, y, w, h, options);
    this.w = w;
    this.h = h;
    Matter.World.add(world, this.body);
  }

  show(p5) {
    const { x, y } = this.body.position;
    p5.push();
    p5.translate(x, y);
    p5.fill(255);
    p5.noStroke();
    p5.rectMode(p5.CENTER);
    p5.rect(0, 0, this.w, this.h);
    p5.pop();
  }
}

let engine;
let world;
let particles = [];
let plinkos = [];
let bounds = [];
let buckets = [];
let bucketWalls = [];
let sideWalls = [];
const processedParticles = new Set();

let plinkoMessageCallback = null;
export const setPlinkoMessageCallback = (cb) => { plinkoMessageCallback = cb; };

export const setup = (p5, canvasParentRef) => {
  console.log('Setup called');
  p5.createCanvas(500, 455).parent(canvasParentRef);
  p5.colorMode(p5.HSB);

  engine = Matter.Engine.create();
  engine.world.gravity.y = 1.5;
  world = engine.world;

  const spacing = p5.width / 12;

  for (let j = 0; j < 10; j++) {
    for (let i = 0; i <= 11; i++) {
      let x = i * spacing;
      let y = spacing + j * spacing;
      if (j % 2 === 0) {
        x += spacing / 2;
      }
      const p = new Plinko(x, y, 8, world);
      plinkos.push(p);
    }
  }

  const ground = new Boundary(p5.width / 2, p5.height + 50, p5.width, 100, world);
  bounds.push(ground);

  for (let i = 0; i < 12; i++) {
    const x = i * spacing;
    const h = 50;
    const w = 10;
    const y = p5.height - h / 2;
    const b = new Boundary(x, y, w, h, world);
    bounds.push(b);
  }

  const leftWall = new Boundary(-5, p5.height / 2, 10, p5.height, world, 'boundary');
  const rightWall = new Boundary(p5.width + 5, p5.height / 2, 10, p5.height, world, 'boundary');
  sideWalls.push(leftWall, rightWall);

  for (let i = 0; i < 11; i++) {
    const x = i * spacing + spacing / 2;
    const y = p5.height - 10;
    const bucket = new Boundary(x, y, spacing, 50, world, 'bucket');
    buckets.push(bucket);

    const leftWall = new Boundary(x - spacing / 2, y - 25, 10, 50, world, 'bucketWall');
    const rightWall = new Boundary(x + spacing / 2, y - 25, 10, 50, world, 'bucketWall');
    bucketWalls.push(leftWall, rightWall);
  }

  console.log('Setup complete');
};

export const addParticle = () => {
  if (!world) {
    console.error('World not initialized');
    return;
  }
  const p = new Particle(300, 0, 8, world);
  particles.push(p);
};

export const draw = (p5) => {
  if (!engine || !world) {
    console.error('Engine or world not initialized');
    return;
  }
  p5.clear();

  Matter.Engine.update(engine, 1000 / 60);

  for (let i = particles.length - 1; i >= 0; i--) {
    particles[i].show(p5);
    if (particles[i].isOffScreen(p5.width, p5.height)) {
      Matter.World.remove(world, particles[i].body);
      particles.splice(i, 1);
      plinkoMessageCallback?.('You won 💴 0!');
    }
  }

  for (const plinko of plinkos) {
    plinko.show(p5);
  }

  for (const bound of bounds) {
    bound.show(p5);
  }

  for (const bucket of buckets) {
    bucket.show(p5);
  }

  for (const wall of bucketWalls) {
    wall.show(p5);
  }

  for (const wall of sideWalls) {
    wall.show(p5);
  }
};

export const usePlinkoGame = () => {
  const { user, depositMoney, withdrawMoney } = useBank();
  const [balls, setBalls] = useState([]);
  const [message, setMessage] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [betAmount, setBetAmount] = useState(0);
  const [lastRewardTime, setLastRewardTime] = useState(0);

  const betDenominations = useMemo(() => BET_DENOMINATIONS, []);
  const rewards = useMemo(() => [2, 2, 1, 0.5, 0.5, 0, 0.5, 0.5, 1, 2, 2], []);

  const dropBall = async (amount) => {
    if (user.money >= amount) {
      if (balls.length < 10) {
        await depositMoney(amount);
        addParticle();
        setBalls((prevBalls) => [...prevBalls, { amount, id: world.bodies[world.bodies.length - 1].id }]);
        setBetAmount(amount);
      } else {
        setMessage('Maximum number of balls reached.');
      }
    } else {
      setMessage('Insufficient funds to place the bet.');
    }
  };

  useEffect(() => {
    console.log('Setting up collision detection');
    const handleCollision = (event) => {
      const processedDuringThisCycle = new Set();

      event.pairs.forEach(async ({ bodyA, bodyB }) => {
        if ((bodyA.label === 'particle' && bodyB.label === 'bucket') || (bodyB.label === 'particle' && bodyA.label === 'bucket')) {
          const particle = bodyA.label === 'particle' ? bodyA : bodyB;
          const bucket = bodyA.label === 'bucket' ? bodyA : bodyB;
          if (!processedParticles.has(particle.id) && !processedDuringThisCycle.has(particle.id)) {
            const currentTime = Date.now();
            if (currentTime - lastRewardTime > 50) {
              setLastRewardTime(currentTime);
              const slotIndex = Math.floor(bucket.position.x / (600 / rewards.length));
              const reward = parseFloat((rewards[slotIndex] * betAmount).toFixed(2));
              try {
                processedParticles.add(particle.id);
                Matter.World.remove(world, particle);
                particles = particles.filter(p => p.body.id !== particle.id);
                setBalls((prevBalls) => prevBalls.filter(ball => ball.id !== particle.id));
                processedDuringThisCycle.add(particle.id);
                const payoutSuccess = await withdrawMoney(reward);
                if (payoutSuccess) {
                  console.log(`Payout: User won ${reward}`);
                  setMessage(`You won 💴 ${reward}!`);
                  setGameOver(true);
                }
              } catch (error) {
                console.error('Error withdrawing money:', error);
              }
            }
          }
        } else if ((bodyA.label === 'particle' && bodyB.label === 'bucketWall') || (bodyB.label === 'particle' && bodyA.label === 'bucketWall')) {
          console.log(`Collision with bucket wall: Particle ${bodyA.label === 'particle' ? bodyA.id : bodyB.id}`);
        } else if ((bodyA.label === 'particle' && bodyB.label === 'boundary') || (bodyB.label === 'particle' && bodyA.label === 'boundary')) {
          const particle = bodyA.label === 'particle' ? bodyA : bodyB;
          if (!processedParticles.has(particle.id) && !processedDuringThisCycle.has(particle.id)) {
            if (particle.position.y > 550) {
              if (!particle.isMoving) {
                particle.isMoving = true;
                setTimeout(() => {
                  if (particle.isMoving) {
                    Matter.World.remove(world, particle);
                    particles = particles.filter(p => p.body.id !== particle.id);
                    setBalls((prevBalls) => prevBalls.filter(ball => ball.id !== particle.id));
                    processedParticles.add(particle.id);
                    processedDuringThisCycle.add(particle.id);
                    console.log('Stuck particle removed:', particle.id);
                  }
                }, 1000);
              }
            } else {
              Matter.World.remove(world, particle);
              particles = particles.filter(p => p.body.id !== particle.id);
              setBalls((prevBalls) => prevBalls.filter(ball => ball.id !== particle.id));
              processedParticles.add(particle.id);
              processedDuringThisCycle.add(particle.id);
              console.log('Stuck particle removed:', particle.id);
            }
          }
        }
      });
    };

    Matter.Events.on(engine, 'collisionStart', handleCollision);
    setPlinkoMessageCallback(setMessage);

    return () => {
      setPlinkoMessageCallback(null);
      Matter.Events.off(engine, 'collisionStart', handleCollision);
    };
  }, [betAmount, rewards, withdrawMoney, lastRewardTime]);

  const handleBet = (amount) => {
    dropBall(amount);
  };

  const resetGame = () => {
    balls.forEach(ball => Matter.World.remove(world, ball.body));
    setBalls([]);
    setGameOver(false);
    setMessage('');
    setBetAmount(0);
  };

  return {
    user,
    betDenominations,
    handleBet,
    resetGame,
    message,
    gameOver,
  };
};
